import { Box, Flex, MenuItem, useDisclosure } from '@chakra-ui/react';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { CompositionLocationData } from '../../compositions/shared/hooks/useCompositionGroup';
import { Icons, defaultIconSize } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { ArchivedCompositionGroupsModal } from '../../compositions/shared/group/ArchivedCompositionGroupsModal';
import { OptionsList } from '../shared/OptionsList';
import { OptionsListContainer } from '../shared/OptionsListContainer';
import { CompositionGroupContainer } from '../shared/CompositionGroupContainer';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useOptionsOverview } from './useOptionsOverview';
import { SelectVdcTemplate } from '../vdc/SelectVdcTemplate';

export function StandardArticleOptions({
  variantId,
  isVdc,
}: {
  variantId: number;
  isVdc: boolean;
}) {
  const { t } = useTranslation();
  const options = useOptionsOverview({
    variantId: variantId,
  });

  const {
    createGroupRequest,
    versionsLoading,
    versions,
    locationData,
    archiveGroupRequest,
    isCreating,
    copyGroup,
  } = options;
  const {
    isOpen: isNewGroupOpen,
    onClose: onNewGroupClose,
    onOpen: onNewGroupOpen,
  } = useDisclosure();
  const {
    isOpen: isNewGroupFromTemplateOpen,
    onClose: onNewGroupFromTemplateClose,
    onOpen: onNewGroupFromTemplateOpen,
  } = useDisclosure();
  const {
    value: archivedGroupsLocationValue,
    isOpen: isShowArchivedOpen,
    onClose: onShowArchivedClose,
    onOpen: onShowArchivedOpen,
  } = useValueDisclosure<CompositionLocationData>();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  useEffect(() => {
    if (selectedOption === null && versions && versions.length > 0)
      setSelectedOption(versions[0].id);
  }, [versions, selectedOption, setSelectedOption]);

  const option = versions?.find((x) => x.id === selectedOption);

  return (
    <>
      <Flex pt={4} gap={2}>
        <OptionsListContainer
          menuItems={
            <>
              {!isVdc ? (
                <MenuItem
                  icon={<Icons.Plus boxSize={defaultIconSize} />}
                  onClick={() => {
                    if (versions?.length === 0 && !versionsLoading) {
                      createGroupRequest();
                      return;
                    }

                    onNewGroupOpen();
                  }}
                >
                  {t('compositionGroup.option.addNew')}
                </MenuItem>
              ) : (
                <MenuItem
                  icon={<Icons.Plus boxSize={defaultIconSize} />}
                  onClick={onNewGroupFromTemplateOpen}
                >
                  {t('vdc.addNewOptionFromVdcTemplate')}
                </MenuItem>
              )}
              <MenuItem
                onClick={() =>
                  onShowArchivedOpen({
                    variantId: locationData.current.variantId,
                  })
                }
                icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
              >
                {t('compositionGroup.option.archivedOptions')}
              </MenuItem>
            </>
          }
        >
          {isCreating && (
            <SpinnerWithLabel label={t('compositionGroup.option.loading')} />
          )}
          <OptionsList
            groupsLoading={versionsLoading}
            isCreating={isCreating}
            mode={{ hideCreate: isVdc }}
            onCreateClick={() => {
              if (isCreating) return;
              createGroupRequest();
            }}
            groups={versions?.map((x) => ({
              id: x.id,
              fileId: x.fileId,
              version: x.version,
              compositions: x.compositions,
              note: x.note,
              isReady: x.isReady,
            }))}
            selectedOption={selectedOption}
            onClick={setSelectedOption}
          />
        </OptionsListContainer>

        {option && (
          <Box px={4} pb={4} pl={8} key={option.id} w="full">
            <CompositionGroupContainer
              isMatrix={false}
              isVdc={isVdc}
              option={option}
              onArchive={async () => await archiveGroupRequest(option.id)}
              onCopy={async (fromId, copyConfig) =>
                await copyGroup({ fromId, copyConfig })
              }
            />
          </Box>
        )}
      </Flex>
      <VerifyDialogWithFunction
        headerTitle={t('compositionGroup.option.createNew')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        toPerform={() => createGroupRequest()}
        isOpen={isNewGroupOpen}
        onClose={onNewGroupClose}
      >
        {t('composition.newOptionConfirm')}
      </VerifyDialogWithFunction>
      {archivedGroupsLocationValue && (
        <ArchivedCompositionGroupsModal
          locationData={archivedGroupsLocationValue}
          isOpen={isShowArchivedOpen}
          onClose={onShowArchivedClose}
        />
      )}
      <SelectVdcTemplate
        isOpen={isNewGroupFromTemplateOpen}
        onClose={onNewGroupFromTemplateClose}
        variantId={locationData.current.variantId}
      />
    </>
  );
}
